import { useAppSelector } from 'app/hooks';
import { SystemOptionType } from 'data/types';
import { DuctedComponentSwapHandler } from 'hooks/use-ducted-component-swap';
import { useEditableSystemMap } from 'hooks/use-editable-system-map';
import { useMemo } from 'react';
import { DuctedSwapLockState } from 'utils/ducted-component-swap-types';
import { isDuctedCompatibilitySystem } from 'utils/ducted-quote-layout';
import { isPtacSelectedSystem } from 'utils/ptac-selected-system';
import CondenserComponentGroup from './condenser-component-group';
import DuctedSystemComponentsTable from './ducted-system-components-table';
import PtacSystemComponentsTable from './ptac-system-components-table';

type SystemComponentsTableProps = Readonly<{
  option: SystemOptionType;
  onCustomSystemSaved?: (uniqueId: string) => void;
  onDuctedComponentSwap?: DuctedComponentSwapHandler;
  ductedLockState?: DuctedSwapLockState;
}>;

type ConfiguredSystemComponentsTableProps = Readonly<{
  option: SystemOptionType;
  onCustomSystemSaved?: (uniqueId: string) => void;
}>;

function ConfiguredSystemComponentsTable({
  option,
  onCustomSystemSaved
}: ConfiguredSystemComponentsTableProps) {
  const { configurations } = useAppSelector((state) => state.configurations);
  const { getDisplayCondenser, makeComponentSwapHandler } =
    useEditableSystemMap(option, onCustomSystemSaved);

  const fullConfig = useMemo(
    () =>
      configurations.find(
        (c) => c.uniqueId === option.configuration.uniqueId
      ) ?? null,
    [configurations, option.configuration.uniqueId]
  );

  return (
    <div className='flex flex-col gap-4'>
      {option.configuration.condensers.map((condenser) => {
        const validSystems =
          fullConfig?.condensers.find((c) => c.uniqueId === condenser.uniqueId)
            ?.validSystems ?? [];
        return (
          <CondenserComponentGroup
            key={condenser.uniqueId}
            condenser={getDisplayCondenser(condenser)}
            validSystems={validSystems}
            onComponentSwap={makeComponentSwapHandler(condenser.uniqueId)}
          />
        );
      })}
    </div>
  );
}

export default function SystemComponentsTable({
  option,
  onCustomSystemSaved,
  onDuctedComponentSwap,
  ductedLockState
}: SystemComponentsTableProps) {
  if (isPtacSelectedSystem(option)) {
    return (
      <PtacSystemComponentsTable
        option={option}
        onCustomSystemSaved={onCustomSystemSaved}
      />
    );
  }
  if (isDuctedCompatibilitySystem(option)) {
    return (
      <DuctedSystemComponentsTable
        option={option}
        onComponentSwap={onDuctedComponentSwap}
        lockState={ductedLockState}
      />
    );
  }
  return (
    <ConfiguredSystemComponentsTable
      option={option}
      onCustomSystemSaved={onCustomSystemSaved}
    />
  );
}
